export interface Contact {
  id: string;
  location_id: string;
  location_name: string;
  name: string;
  email: string;
  phone: string | null;
  role: string | null;
  is_primary: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  access_state: "editable_legacy" | "protected_canonical";
  can_edit: boolean;
  can_select: boolean;
  can_delete: boolean;
  detail_loaded: boolean;
  restriction_reason: string | null;
  is_redacted: boolean;
  email_hint?: string | null;
  mutation_outcome?: "created" | "existing" | "updated";
}
export interface ContactInsert {
  location_id: string;
  name: string;
  email: string;
  phone?: string | null;
  role?: string | null;
  is_primary?: boolean;
  notes?: string | null;
}
export type ContactUpdate = Partial<Omit<ContactInsert, "location_id">>;
export class ContactError extends Error {
  constructor(
    message: string,
    public readonly kind:
      | "read"
      | "denied"
      | "conflict"
      | "invalid"
      | "unknown"
      | "committed",
    public readonly contactId?: string,
  ) {
    super(message);
    this.name = "ContactError";
  }
}
function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new ContactError(
      "Contact response could not be read. Reload contacts.",
      "read",
    );
  return value as Record<string, unknown>;
}
export function mapAdminContact(value: unknown, detail = false): Contact {
  const row = object(value);
  const text = (key: string) => {
    if (typeof row[key] !== "string")
      throw new ContactError(
        "Contact response could not be read. Reload contacts.",
        "read",
      );
    return row[key] as string;
  };
  const nullable = (key: string) => {
    if (row[key] === null) return null;
    return text(key);
  };
  const bool = (key: string) => {
    if (typeof row[key] !== "boolean")
      throw new ContactError(
        "Contact response could not be read. Reload contacts.",
        "read",
      );
    return row[key] as boolean;
  };
  const state = text("access_state");
  if (state !== "editable_legacy" && state !== "protected_canonical")
    throw new ContactError("Contact access changed. Reload contacts.", "read");
  if (bool("detail_loaded") !== detail)
    throw new ContactError(
      "Contact detail could not be read. Reload contacts.",
      "read",
    );
  const protectedRow = state === "protected_canonical";
  const contact: Contact = {
    id: text("id"),
    location_id: text("location_id"),
    location_name: text("location_name"),
    name: protectedRow ? "Protected contact" : text("name"),
    email: protectedRow ? "" : text("email"),
    role: protectedRow ? null : nullable("role"),
    phone: detail && !protectedRow ? nullable("phone") : null,
    notes: detail && !protectedRow ? nullable("notes") : null,
    is_primary: bool("is_primary"),
    created_at: text("created_at"),
    updated_at: text("updated_at"),
    created_by: null,
    access_state: state,
    can_edit: bool("can_edit") && !protectedRow,
    can_select: bool("can_select") && !protectedRow,
    can_delete: bool("can_delete") && !protectedRow,
    is_redacted: protectedRow,
    detail_loaded: detail,
    restriction_reason: protectedRow
      ? "Requires reviewed identity workflow."
      : nullable("restriction_reason"),
  };
  return contact;
}
export type ContactRpc = (
  name: string,
  args: Record<string, unknown>,
) => PromiseLike<{
  data: unknown;
  error: { code?: string; message?: string } | null;
}>;
export function createContactsService(
  rpc: ContactRpc,
  generation: () => number = () => 0,
) {
  async function call(
    name: string,
    args: Record<string, unknown>,
    write = false,
  ) {
    const started = generation();
    let result;
    try {
      result = await rpc(name, args);
    } catch {
      if (started !== generation())
        throw new ContactError("Session changed. Reload contacts.", "denied");
      throw new ContactError(
        write
          ? "Save outcome unknown. Reload contacts before retrying."
          : "Contacts could not be loaded. Retry.",
        write ? "unknown" : "read",
      );
    }
    if (started !== generation())
      throw new ContactError("Session changed. Reload contacts.", "denied");
    if (result.error) {
      const code = result.error.code;
      if (code === "42501")
        throw new ContactError("Admin contact access required.", "denied");
      if (code === "PT409" || code === "40001")
        throw new ContactError(
          "Contact changed. Reload before saving.",
          "conflict",
        );
      if (code === "22023")
        throw new ContactError(
          "Check contact name, email, and field values.",
          "invalid",
        );
      if (code === "P0001" || code === "P0002")
        throw new ContactError(
          code === "P0002"
            ? "Contact no longer exists. Reload contacts."
            : "Contact operation restricted. Reload to check its access and references.",
          "denied",
        );
      throw new ContactError(
        write
          ? "Save outcome unknown. Reload contacts before retrying."
          : "Contacts could not be loaded. Retry.",
        write ? "unknown" : "read",
      );
    }
    return result.data;
  }
  async function list(locationId: string | null) {
    const data = await call("list_admin_location_contacts", {
      p_location_id: locationId,
    });
    if (!Array.isArray(data))
      throw new ContactError("Contacts could not be loaded. Retry.", "read");
    return data.map((row) => mapAdminContact(row));
  }
  async function mutate(name: string, args: Record<string, unknown>) {
    const receipt = await call(name, args, true);
    if (!receipt || typeof receipt !== "object" || Array.isArray(receipt))
      throw new ContactError(
        "Save outcome unknown. Reload contacts before retrying.",
        "unknown",
      );
    const data = receipt as Record<string, unknown>;
    if (typeof data.contact_id !== "string")
      throw new ContactError(
        "Save outcome unknown. Reload contacts before retrying.",
        "unknown",
      );
    if (!["created", "existing", "updated"].includes(String(data.outcome)))
      throw new ContactError(
        "Save outcome unknown. Reload contacts before retrying.",
        "unknown",
      );
    try {
      const contact = mapAdminContact(data.contact, true);
      if (
        contact.id !== data.contact_id ||
        !["created", "existing", "updated"].includes(String(data.outcome))
      )
        throw new Error("Invalid receipt");
      return {
        ...contact,
        mutation_outcome: data.outcome as Contact["mutation_outcome"],
      };
    } catch {
      throw new ContactError(
        "Contact saved. Reload saved details before editing again.",
        "committed",
        data.contact_id,
      );
    }
  }
  type Recovery = {
    kind: "committed" | "unknown";
    id?: string;
    outcome?: Contact["mutation_outcome"];
  };
  const pending = new Map<string, Recovery>();
  let recoveryGeneration = generation();
  const commandKey = (name: string, args: Record<string, unknown>) => {
    if (recoveryGeneration !== generation()) {
      pending.clear();
      recoveryGeneration = generation();
    }
    return name + JSON.stringify(args);
  };
  async function read(id: string) {
    const data = await call("get_admin_location_contact", { p_contact_id: id });
    return data === null ? null : mapAdminContact(data, true);
  }
  async function recoverableMutation(
    name: string,
    args: Record<string, unknown>,
  ): Promise<Contact> {
    const commandGeneration = generation();
    const assertCurrent = () => {
      if (commandGeneration !== generation())
        throw new ContactError("Session changed. Reload contacts.", "denied");
    };
    const key = commandKey(name, args);
    const previous = pending.get(key);
    if (previous?.kind === "committed" && previous.id) {
      const row = await read(previous.id);
      assertCurrent();
      if (!row)
        throw new ContactError(
          "Saved contact no longer exists. Reload contacts.",
          "denied",
          previous.id,
        );
      pending.delete(key);
      return { ...row, mutation_outcome: previous.outcome };
    }
    if (previous?.kind === "unknown") {
      if (name === "create_admin_location_contact") {
        const fields = args.p_contact as ContactUpdate;
        const rows = await list(args.p_location_id as string);
        assertCurrent();
        const matches = rows.filter(
          (row) =>
            row.can_select &&
            row.name.trim().toLowerCase() ===
              fields.name?.trim().toLowerCase() &&
            row.email.trim().toLowerCase() ===
              fields.email?.trim().toLowerCase(),
        );
        if (matches.length > 1)
          throw new ContactError(
            "Multiple contact matches. Review location contacts.",
            "denied",
          );
        if (matches.length === 1) {
          const row = await read(matches[0]!.id);
          assertCurrent();
          if (!row)
            throw new ContactError("Reload contacts before retrying.", "read");
          pending.delete(key);
          return { ...row, mutation_outcome: "existing" };
        }
      } else {
        const row = await read(args.p_contact_id as string);
        assertCurrent();
        if (!row || row.updated_at !== args.p_expected_updated_at)
          throw new ContactError(
            "Contact changed. Reload before saving.",
            "conflict",
          );
      }
      pending.delete(key);
    }
    assertCurrent();
    try {
      const row = await mutate(name, args);
      assertCurrent();
      return row;
    } catch (failure) {
      assertCurrent();
      if (
        failure instanceof ContactError &&
        (failure.kind === "committed" || failure.kind === "unknown")
      ) {
        if (pending.size >= 100) pending.clear();
        pending.set(key, {
          kind: failure.kind,
          id: failure.contactId,
          outcome:
            name === "create_admin_location_contact" ? "existing" : "updated",
        });
      }
      throw failure;
    }
  }
  return {
    getAll: () => list(null),
    getByLocationId: (id: string) => list(id),
    resetRecovery() {
      pending.clear();
    },
    getById: read,
    create({ location_id, ...data }: ContactInsert) {
      return recoverableMutation("create_admin_location_contact", {
        p_location_id: location_id,
        p_contact: data,
      });
    },
    update(id: string, data: ContactUpdate, expectedUpdatedAt: string) {
      if (!expectedUpdatedAt)
        throw new ContactError(
          "Load contact details before saving.",
          "invalid",
        );
      return recoverableMutation("update_admin_location_contact", {
        p_contact_id: id,
        p_expected_updated_at: expectedUpdatedAt,
        p_changes: data,
      });
    },
    async delete(id: string, expectedUpdatedAt: string) {
      if (!expectedUpdatedAt)
        throw new ContactError(
          "Load contact details before deleting.",
          "invalid",
        );
      const data = object(
        await call(
          "delete_admin_location_contact",
          { p_contact_id: id, p_expected_updated_at: expectedUpdatedAt },
          true,
        ),
      );
      if (data.contact_id !== id || data.outcome !== "deleted")
        throw new ContactError(
          "Delete outcome unknown. Reload contacts before retrying.",
          "unknown",
        );
    },
  };
}
